// LAP-550 — Card Conseil : coach analyste de portefeuille (Life Agent).
// Trame identique au chat du Conseiller Patrimonial (wealth_advisor_chat) :
//   POST /api/investment/portfolio-coach/chat/
//     body:   { message?, locale, history: [{role, content}, ...] }
//     output: { reply, source, locale, generated_at }
//     coût:   1 crédit (feature `portfolio_coach`)
//     erreurs: 402 → la modale globale "Crédits insuffisants" prend le relais.
// Sans `message`, le backend renvoie l'ANALYSE COMPLÈTE du portefeuille ;
// ensuite l'utilisateur approfondit en posant ses questions dans le fil.
import { Box, Button, CircularProgress, IconButton, InputBase } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import CardHeader from '../../_shared/CardHeader'
import { invTheme } from '../../_shared/theme'
import { makePostRequest } from 'utils/api'
import { loadWallet } from '../../../../Components/Billing/billingThunks'

export default function AdviceCard() {
  const { t, i18n } = useTranslation('layout')
  const dispatch = useDispatch()
  const [messages, setMessages] = useState([]) // [{role: 'user'|'agent', content}]
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const scrollRef = useRef(null)

  // Le fil reste lisible : on colle en bas à chaque nouveau message.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight
  }, [messages, loading])

  const send = async (text) => {
    if (loading) return
    const message = (text || '').trim()
    setError(null)
    const history = messages.map((m) => ({ role: m.role, content: m.content }))
    if (message) setMessages((prev) => [...prev, { role: 'user', content: message }])
    setLoading(true)
    try {
      const response = await makePostRequest(
        '/api/investment/portfolio-coach/chat/',
        { message, locale: i18n.language, history, feature: 'portfolio_coach' },
        {},
        true,
      )
      const reply = response?.data?.reply || response?.data?.content
      if (reply) setMessages((prev) => [...prev, { role: 'agent', content: reply }])
      else setError(t('investments.portfolio.advice.error'))
      // Le message a coûté 1 crédit : refresh du wallet, silencieux si KO.
      try {
        await dispatch(loadWallet())
      } catch (_) {
        /* silencieux */
      }
    } catch (e) {
      // 402 → la modale globale "Crédits insuffisants" s'affiche déjà.
      if (e?.response?.status !== 402) setError(t('investments.portfolio.advice.error'))
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!input.trim() || loading) return
    const text = input
    setInput('')
    send(text)
  }

  const started = messages.length > 0 || loading

  return (
    <Box display="flex" flexDirection="column" height="100%" minHeight={0}>
      <CardHeader
        icon={<AutoAwesomeOutlinedIcon fontSize="small" />}
        title={t('investments.portfolio.advice.title')}
        subtitle="AI Insight"
      />
      <Box
        mt={2}
        flex={1}
        minHeight={0}
        width="100%"
        display="flex"
        flexDirection="column"
        sx={{
          background:
            'radial-gradient(circle at 30% 0%, rgba(167,139,250,0.12), transparent 65%)',
          borderRadius: 2,
          p: 1.5,
          border: '1px solid rgba(167,139,250,0.18)',
        }}
      >
        {!started ? (
          // État initial : pitch + bouton d'analyse (1 crédit).
          <Box
            flex={1}
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            textAlign="center"
            px={1}
          >
            <Box sx={{ fontSize: 14, lineHeight: 1.55, color: 'rgba(255,255,255,0.85)' }}>
              {t('investments.portfolio.advice.intro')}
            </Box>
            <Button
              onClick={() => send('')}
              variant="outlined"
              size="small"
              startIcon={<AutoAwesomeOutlinedIcon />}
              sx={{
                mt: 2.5,
                color: invTheme.accents.advice,
                borderColor: 'rgba(167,139,250,0.45)',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  borderColor: invTheme.accents.advice,
                  backgroundColor: 'rgba(167,139,250,0.10)',
                },
              }}
            >
              {t('investments.portfolio.advice.cta')}
            </Button>
            <Box sx={{ mt: 1.25, fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>
              {t('investments.portfolio.advice.costHint')}
            </Box>
          </Box>
        ) : (
          <>
            {/* Fil de la conversation, scrollable dans la card. */}
            <Box ref={scrollRef} flex={1} minHeight={0} sx={{ overflowY: 'auto', pr: 0.5 }}>
              {messages.map((m, i) =>
                m.role === 'agent' ? (
                  <Box
                    key={i}
                    sx={{
                      fontSize: 13.5,
                      lineHeight: 1.55,
                      color: 'rgba(255,255,255,0.88)',
                      whiteSpace: 'pre-wrap',
                      borderLeft: `2px solid ${invTheme.accents.advice}`,
                      pl: 1.25,
                      my: 1,
                    }}
                  >
                    {m.content}
                    <Box
                      sx={{
                        textAlign: 'right',
                        mt: 0.5,
                        fontSize: 11,
                        color: invTheme.accents.advice,
                        fontWeight: 600,
                        letterSpacing: 0.5,
                      }}
                    >
                      — {t('investments.portfolio.advice.signature')}
                    </Box>
                  </Box>
                ) : (
                  <Box
                    key={i}
                    sx={{
                      fontSize: 13.5,
                      lineHeight: 1.5,
                      color: '#cfe1ff',
                      backgroundColor: 'rgba(99,160,255,0.10)',
                      border: '1px solid rgba(99,160,255,0.18)',
                      borderRadius: 2,
                      px: 1.25,
                      py: 0.75,
                      my: 1,
                      ml: 4,
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {m.content}
                  </Box>
                ),
              )}
              {loading && (
                <Box
                  display="flex"
                  alignItems="center"
                  sx={{ my: 1, color: 'rgba(255,255,255,0.6)', fontSize: 12.5 }}
                >
                  <CircularProgress size={14} sx={{ mr: 1, color: invTheme.accents.advice }} />
                  {t('investments.portfolio.advice.loading')}
                </Box>
              )}
              {error && (
                <Box sx={{ my: 1, color: '#f87171', fontSize: 12.5 }}>{error}</Box>
              )}
            </Box>
            {/* Saisie d'une question de suivi. */}
            <Box
              component="form"
              onSubmit={handleSubmit}
              display="flex"
              alignItems="center"
              sx={{
                mt: 1,
                borderTop: '1px solid rgba(255,255,255,0.08)',
                pt: 1,
              }}
            >
              <InputBase
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t('investments.portfolio.advice.inputPlaceholder')}
                fullWidth
                sx={{ fontSize: 13, color: 'rgba(255,255,255,0.9)', px: 0.5 }}
              />
              <IconButton
                type="submit"
                size="small"
                disabled={loading || !input.trim()}
                sx={{ color: invTheme.accents.advice }}
              >
                <SendRoundedIcon fontSize="small" />
              </IconButton>
            </Box>
          </>
        )}
      </Box>
    </Box>
  )
}
